<template>
  <!--
    The board: eleven columns of spaces, bottom-aligned so the whole thing
    reads as the classic Can't Stop pyramid. Each column is a <button> when
    nothing needs choosing (inert) - the choosing happens in the action tray,
    not by tapping the board - so the board is purely a display, which keeps
    every input in one place on a phone.
  -->
  <div class="board" role="img" :aria-label="ariaSummary">
    <div
      v-for="column in columns"
      :key="column"
      class="board__column"
      :class="{
        'board__column--claimed': claimedBy(column),
        'board__column--hot': isHot(column),
      }"
    >
      <span class="board__sum" :style="claimedStyle(column)">{{ column }}</span>
      <div class="board__spaces">
        <div
          v-for="step in stepsOf(column)"
          :key="step"
          class="board__space"
          :class="{ 'board__space--top': step === heightOf(column) }"
        >
          <!-- The white runner: this turn's progress, unbanked. -->
          <span
            v-if="runnerAt(column, step)"
            class="runner"
            :class="{ 'runner--top': step === heightOf(column) }"
          />
          <!-- Committed markers, every player's. Several can share a space. -->
          <span
            v-for="player in markersAt(column, step)"
            :key="player.id"
            class="marker"
            :style="{ background: colorOf(player) }"
            :title="player.name"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { COLUMNS, COLUMN_HEIGHTS } from '@/engine/constants';

// Seat colors, in seat order. The rules hand out "red, green, blue, or
// yellow" (p1, Setup); these are those four, tuned to stay readable on the
// board's dark red - which is also why the claimant's own red is the
// brightest thing here rather than the board's brick shade.
export const SEAT_COLORS = ['#4aa3ff', '#ffc848', '#43c47c', '#ff5a4e'];

export default {
  name: 'GameBoard',
  props: {
    game: { type: Object, required: true },
  },
  computed: {
    columns() { return COLUMNS; },
    runners() { return this.game.turn.runners || {}; },
    ariaSummary() {
      const claimed = Object.entries(this.game.columns)
        .filter(([, owner]) => owner)
        .map(([column]) => column);
      return claimed.length
        ? `Can't Stop board, columns ${claimed.join(', ')} claimed`
        : "Can't Stop board, no columns claimed yet";
    },
  },
  methods: {
    heightOf(column) { return COLUMN_HEIGHTS[column]; },
    // Steps top-down, so the DOM orders naturally with the summit first.
    stepsOf(column) {
      const height = COLUMN_HEIGHTS[column];
      return Array.from({ length: height }, (_, i) => height - i);
    },
    claimedBy(column) { return this.game.columns[column] || null; },
    claimedStyle(column) {
      const owner = this.claimedBy(column);
      if (!owner) return {};
      const player = this.game.players.find((p) => p.id === owner);
      return player ? { background: this.colorOf(player), color: '#14100f' } : {};
    },
    isHot(column) { return this.runners[column] !== undefined; },
    runnerAt(column, step) { return this.runners[column] === step; },
    markersAt(column, step) {
      return this.game.players.filter((p) => p.markers?.[column] === step);
    },
    colorOf(player) { return SEAT_COLORS[player.seat % SEAT_COLORS.length]; },
  },
};
</script>
